import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { 
  Box, TextField, Button, Typography, Paper, Container, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Divider, IconButton, Tooltip, Stack, CircularProgress, InputAdornment,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, 
  ListItemText, Checkbox
} from '@mui/material';
import axios from '../../api/axiosConfig';
import GroupIcon from '@mui/icons-material/Group';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import VisibilityIcon from '@mui/icons-material/Visibility';

const GroupManage = () => {
  const API_URL = '/api/admin/groups';
  const THEME_NAVY = '#1a233a';
  
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedGroupPk, setSelectedGroupPk] = useState(null);

  const [allUsers, setAllUsers] = useState([]); 
  const [allCategories, setAllCategories] = useState([]); 
  const [selectedUserPks, setSelectedUserPks] = useState([]); 
  const [selectedCategoryPks, setSelectedCategoryPks] = useState([]); 

  const [filterText, setFilterText] = useState('');
  const [formData, setFormData] = useState({ name: '', description: '' });

  // --- 팝업 관련 상태 ---
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); 
  const [dialogSearch, setDialogSearch] = useState('');
  const [tempCheckedPks, setTempCheckedPks] = useState([]);

  // --- 상세 보기 팝업 상태 ---
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewData, setViewData] = useState({ name: '', users: [], categories: [] });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, catRes] = await Promise.all([
          axios.get('/api/admin/users'),
          axios.get('/api/category')
        ]);
        setAllUsers(userRes.data || []);
        setAllCategories(catRes.data || []);
      } catch (error) {
        console.error("기본 데이터 로딩 실패", error);
      }
    };
    fetchData();
  }, []);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await axios.get(API_URL, { params: { search: filterText } });
      setGroups(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("그룹 로딩 실패:", error);
    } finally {
      setLoading(false);
    }
  }, [filterText]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const saveDto = {
        groupPk: isEditMode ? selectedGroupPk : null,
        name: formData.name,
        description: formData.description,
        userPks: selectedUserPks,
        categoryPks: selectedCategoryPks
      };
      await axios.post(`${API_URL}/save`, saveDto);
      alert(isEditMode ? '수정되었습니다.' : '등록되었습니다.');
      resetForm();
      fetchGroups();
    } catch (error) {
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  const handleEdit = async (group) => {
    setIsEditMode(true);
    setSelectedGroupPk(group.groupPk);
    setFormData({ name: group.name, description: group.description || '' });
    try {
      const response = await axios.get(`${API_URL}/${group.groupPk}/mappings`);
      setSelectedUserPks(response.data.userPks || []);
      setSelectedCategoryPks(response.data.categoryPks || []);
    } catch (error) {
      console.error("매핑 정보 로드 실패", error);
    }
  };

  const handleViewDetails = (group) => {
    const groupUsers = allUsers.filter(u => group.userPks?.includes(u.userPk));
    const groupCats = allCategories.filter(c => group.categoryPks?.includes(c.categoryPk));
    setViewData({ name: group.name, users: groupUsers, categories: groupCats });
    setViewDialogOpen(true);
  };

  const resetForm = () => {
    setIsEditMode(false);
    setSelectedGroupPk(null);
    setFormData({ name: '', description: '' });
    setSelectedUserPks([]);
    setSelectedCategoryPks([]);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour12: false });
  };

  const handleOpenDialog = (type) => {
    setDialogType(type);
    setDialogSearch('');
    setTempCheckedPks([]);
    setDialogOpen(true);
  };

  const toggleTempCheck = (pk) => {
    setTempCheckedPks(prev => prev.includes(pk) ? prev.filter(id => id !== pk) : [...prev, pk]);
  };

  const handleMoveRight = () => {
    if (dialogType === 'user') setSelectedUserPks(prev => [...new Set([...prev, ...tempCheckedPks])]);
    else setSelectedCategoryPks(prev => [...new Set([...prev, ...tempCheckedPks])]);
    setTempCheckedPks([]);
  };

  const handleMoveLeft = () => {
    if (dialogType === 'user') setSelectedUserPks(prev => prev.filter(pk => !tempCheckedPks.includes(pk)));
    else setSelectedCategoryPks(prev => prev.filter(pk => !tempCheckedPks.includes(pk)));
    setTempCheckedPks([]);
  };

  const dialogData = useMemo(() => {
    const isUser = dialogType === 'user';
    const all = isUser ? allUsers : allCategories;
    const selected = isUser ? selectedUserPks : selectedCategoryPks;
    const pkField = isUser ? 'userPk' : 'categoryPk';
    const left = all.filter(item => !selected.includes(item[pkField]) && (isUser ? `${item.name}${item.id}` : item.name).toLowerCase().includes(dialogSearch.toLowerCase()));
    const right = all.filter(item => selected.includes(item[pkField]));
    return { left, right, pkField, isUser };
  }, [dialogType, allUsers, allCategories, selectedUserPks, selectedCategoryPks, dialogSearch]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <GroupIcon sx={{ mr: 1, color: THEME_NAVY, fontSize: 30 }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: THEME_NAVY }}>사용자 그룹 관리</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* --- 좌측 섹션 --- */}
        <Paper elevation={3} sx={{ p: 2.5, width: { xs: '100%', lg: '380px' }, height: 'fit-content', borderTop: isEditMode ? '4px solid #ed6c02' : `4px solid ${THEME_NAVY}`, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
              {isEditMode ? <EditIcon sx={{ mr: 1, fontSize: 20 }} color="warning" /> : <AddCircleOutlineIcon sx={{ mr: 1, fontSize: 20 }} color="primary" />}
              {isEditMode ? '그룹 수정' : '신규 그룹 등록'}
            </Typography>
            {isEditMode && <IconButton size="small" onClick={resetForm}><CloseIcon fontSize="small" /></IconButton>}
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          
          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="그룹명" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} margin="dense" required size="small" />
            <TextField fullWidth label="그룹 설명" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} margin="dense" size="small" multiline rows={2} />
            
            <Stack sx={{ mt: 2 }} spacing={1}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                <PersonIcon sx={{ fontSize: 16, mr: 0.5 }} /> 배정된 사용자 ({selectedUserPks.length})
              </Typography>
              <Paper variant="outlined" sx={{ height: 130, overflow: 'auto', bgcolor: '#fcfcfc' }}>
                <List dense disablePadding>
                  {selectedUserPks.length === 0 ? <ListItem><ListItemText secondaryTypographyProps={{ variant: 'caption' }} secondary="배정 없음" /></ListItem> :
                    allUsers.filter(u => selectedUserPks.includes(u.userPk)).map(user => (
                      <ListItem key={user.userPk} sx={{ py: 0, px: 1, borderBottom: '1px solid #f0f0f0' }}>
                        <ListItemText primary={`${user.name} (${user.id})`} primaryTypographyProps={{ variant: 'caption', fontWeight: 500 }} />
                      </ListItem>
                    ))
                  }
                </List>
              </Paper>
              <Button fullWidth size="small" sx={{ fontSize: '0.75rem' }} onClick={() => handleOpenDialog('user')}>사용자 설정</Button>

              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', color: 'text.secondary', mt: 1 }}>
                <CategoryIcon sx={{ fontSize: 16, mr: 0.5 }} /> 배정된 카테고리 ({selectedCategoryPks.length})
              </Typography>
              <Paper variant="outlined" sx={{ height: 130, overflow: 'auto', bgcolor: '#fcfcfc' }}>
                <List dense disablePadding>
                  {selectedCategoryPks.length === 0 ? <ListItem><ListItemText secondaryTypographyProps={{ variant: 'caption' }} secondary="배정 없음" /></ListItem> :
                    allCategories.filter(c => selectedCategoryPks.includes(c.categoryPk)).map(cat => (
                      <ListItem key={cat.categoryPk} sx={{ py: 0, px: 1, borderBottom: '1px solid #f0f0f0' }}>
                        <ListItemText primary={cat.name} primaryTypographyProps={{ variant: 'caption', fontWeight: 500 }} />
                      </ListItem>
                    ))
                  }
                </List>
              </Paper>
              <Button fullWidth size="small" sx={{ fontSize: '0.75rem' }} onClick={() => handleOpenDialog('category')}>카테고리 설정</Button>
            </Stack>

            <Button fullWidth variant="contained" type="submit" sx={{ mt: 3, py: 1, backgroundColor: isEditMode ? '#ed6c02' : THEME_NAVY, fontWeight: 'bold' }}>
              {isEditMode ? '수정 완료' : '그룹 등록'}
            </Button>
          </form>
        </Paper>

        {/* --- 우측 섹션 --- */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Paper elevation={1} sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', backgroundColor: '#fdfdfd' }}>
            <TextField size="small" placeholder="그룹명 검색" value={filterText} onChange={(e) => setFilterText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchGroups()} sx={{ width: 250 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            <Button variant="contained" size="small" onClick={fetchGroups} sx={{ bgcolor: THEME_NAVY }}>조회</Button>
            <Button size="small" startIcon={<RefreshIcon />} onClick={() => { setFilterText(''); setHasSearched(false); setGroups([]); }}>초기화</Button>
          </Paper>

          <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f4f6f8' }}>그룹 정보</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f4f6f8' }}>매핑</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f4f6f8' }}>등록정보</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f4f6f8' }}>수정정보</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f4f6f8' }}>액션</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!hasSearched ? <TableRow><TableCell colSpan={5} align="center" sx={{ py: 10 }}>조회 버튼을 클릭하세요.</TableCell></TableRow> :
                  groups.map((group) => (
                    <TableRow key={group.groupPk} hover sx={{ bgcolor: selectedGroupPk === group.groupPk ? '#fff4e5' : 'inherit' }}>
                      <TableCell><Typography variant="body2" sx={{ fontWeight: 'bold' }}>{group.name}</Typography></TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Chip label={`U:${group.userPks?.length || 0}`} size="small" variant="outlined" />
                          <Chip label={`C:${group.categoryPks?.length || 0}`} size="small" variant="outlined" />
                        </Stack>
                      </TableCell>
                      <TableCell><Typography variant="caption" display="block">{group.createUser}</Typography><Typography variant="caption" color="text.secondary">{formatDateTime(group.createDate)}</Typography></TableCell>
                      <TableCell><Typography variant="caption" display="block">{group.updateUser || '-'}</Typography><Typography variant="caption" color="text.secondary">{formatDateTime(group.updateDate)}</Typography></TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="상세보기"><IconButton size="small" onClick={() => handleViewDetails(group)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="수정"><IconButton size="small" color="primary" onClick={() => handleEdit(group)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="삭제"><IconButton size="small" color="error"><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* --- 설정 팝업 (너비 최적화) --- */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: THEME_NAVY, color: 'white', py: 1.5, fontSize: '1rem' }}>{dialogType === 'user' ? '사용자 배정' : '카테고리 배정'}</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Grid item xs={5.2}>
              <TextField fullWidth size="small" placeholder="검색" value={dialogSearch} onChange={(e) => setDialogSearch(e.target.value)} sx={{ mb: 1 }} />
              <Paper variant="outlined" sx={{ height: 300, overflow: 'auto' }}>
                <List dense disablePadding>
                  {dialogData.left.map(item => (
                    <ListItem key={item[dialogData.pkField]} button onClick={() => toggleTempCheck(item[dialogData.pkField])} sx={{ py: 0, px: 1 }}>
                      <Checkbox size="small" checked={tempCheckedPks.includes(item[dialogData.pkField])} />
                      <ListItemText primaryTypographyProps={{ variant: 'caption' }} primary={dialogData.isUser ? `${item.name}(${item.id})` : item.name} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
            <Grid item xs={1.6}>
              <Stack spacing={1} alignItems="center">
                <IconButton size="small" variant="outlined" onClick={handleMoveRight}><ChevronRightIcon /></IconButton>
                <IconButton size="small" variant="outlined" onClick={handleMoveLeft}><ChevronLeftIcon /></IconButton>
              </Stack>
            </Grid>
            <Grid item xs={5.2}>
              <Box sx={{ height: 40, mb: 1, display: 'flex', alignItems: 'center' }}><Typography variant="caption" sx={{ fontWeight: 'bold' }}>배정 목록 ({dialogData.right.length})</Typography></Box>
              <Paper variant="outlined" sx={{ height: 300, overflow: 'auto', bgcolor: '#f8f9fa' }}>
                <List dense disablePadding>
                  {dialogData.right.map(item => (
                    <ListItem key={item[dialogData.pkField]} button onClick={() => toggleTempCheck(item[dialogData.pkField])} sx={{ py: 0, px: 1 }}>
                      <Checkbox size="small" checked={tempCheckedPks.includes(item[dialogData.pkField])} />
                      <ListItemText primaryTypographyProps={{ variant: 'caption' }} primary={dialogData.isUser ? `${item.name}(${item.id})` : item.name} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}><Button onClick={() => setDialogOpen(false)} variant="contained" size="small" sx={{ bgcolor: THEME_NAVY }}>저장</Button></DialogActions>
      </Dialog>

      {/* --- 상세 보기 팝업 --- */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #eee', fontWeight: 'bold' }}>[{viewData.name}] 매핑 상세</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}><PersonIcon sx={{ fontSize: 18, mr: 0.5 }} /> 배정 사용자</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
              {viewData.users.length > 0 ? viewData.users.map(u => <Chip key={u.userPk} label={`${u.name}(${u.id})`} size="small" />) : <Typography variant="caption" color="text.secondary">없음</Typography>}
            </Box>
            <Typography variant="subtitle2" color="secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}><CategoryIcon sx={{ fontSize: 18, mr: 0.5 }} /> 배정 카테고리</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {viewData.categories.length > 0 ? viewData.categories.map(c => <Chip key={c.categoryPk} label={c.name} size="small" />) : <Typography variant="caption" color="text.secondary">없음</Typography>}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setViewDialogOpen(false)}>닫기</Button></DialogActions>
      </Dialog>
    </Container>
  );
};

export default GroupManage;